import { ApiContext, apiContextFetch } from "@/hooks/api/base"

import {
  SlantaGift,
  SlantaGiftUpdate,
  SlantaHousehold,
  SlantaOrder,
  SlantaOrderUpdate,
  SlantaPayment,
  SlantaProductKey,
} from "./types"

export function fetchSlantaAvailability(context: ApiContext) {
  return apiContextFetch<{ submission_open: boolean; has_orders: boolean }>(context, "slanta/availability")
}

export function fetchSlantaOrders(context: ApiContext) {
  return apiContextFetch<{ orders: SlantaOrder[] }>(context, "slanta/orders")
}

export function createSlantaOrder(context: ApiContext) {
  return apiContextFetch<{ order: SlantaOrder }>(context, "slanta/orders", {
    method: "POST",
    body: JSON.stringify({ order: {} }),
  })
}

export function updateSlantaOrder(context: ApiContext, id: string, order: SlantaOrderUpdate) {
  return apiContextFetch<{ order: SlantaOrder }>(context, `slanta/orders/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ order }),
  })
}

export function fetchSlantaHouseholds(context: ApiContext, search?: string) {
  const query = search ? `?search=${encodeURIComponent(search)}` : ""
  return apiContextFetch<{ households: SlantaHousehold[] }>(context, `slanta/households${query}`)
}

export function createSlantaGift(
  context: ApiContext,
  gift: {
    order_id: string
    household_id: string
    product_key: SlantaProductKey
    assignment_source?: SlantaGift["assignment_source"]
  }
) {
  return apiContextFetch<{ gift: SlantaGift }>(context, "slanta/gifts", {
    method: "POST",
    body: JSON.stringify({ gift }),
  })
}

export function updateSlantaGift(context: ApiContext, id: string, gift: SlantaGiftUpdate) {
  return apiContextFetch<{ gift: SlantaGift }>(context, `slanta/gifts/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ gift }),
  })
}

export function destroySlantaGift(context: ApiContext, id: string) {
  return apiContextFetch<void>(context, `slanta/gifts/${id}`, { method: "DELETE" })
}

export function draftSlantaCard(context: ApiContext, giftId: string, instruction: string) {
  return apiContextFetch<{ gift: SlantaGift }>(context, `slanta/gifts/${giftId}/card_draft`, {
    method: "POST",
    body: JSON.stringify({ card_draft: { instruction } }),
  })
}

export function approveSlantaCard(context: ApiContext, giftId: string) {
  return apiContextFetch<{ gift: SlantaGift }>(context, `slanta/gifts/${giftId}/card_approval`, {
    method: "POST",
    body: JSON.stringify({}),
  })
}

export function approveSlantaCards(context: ApiContext, orderId: string, gifts: Pick<SlantaGift, "id" | "message">[]) {
  return apiContextFetch<{ order: SlantaOrder }>(context, `slanta/orders/${orderId}/card_approval`, {
    method: "POST",
    body: JSON.stringify({ card_approval: { gifts } }),
  })
}

export function submitSlantaOrder(context: ApiContext, orderId: string) {
  return apiContextFetch<{ order: SlantaOrder }>(context, `slanta/orders/${orderId}/submission`, {
    method: "POST",
    body: JSON.stringify({}),
  })
}

export function prepareSlantaPayment(context: ApiContext, orderId: string) {
  return apiContextFetch<{ payment: SlantaPayment }>(context, `slanta/orders/${orderId}/payment`, {
    method: "POST",
    body: JSON.stringify({}),
  })
}

export function cancelSlantaPayment(context: ApiContext, orderId: string, checkoutAttempt: number) {
  return apiContextFetch<{ order: SlantaOrder }>(context, `slanta/orders/${orderId}/payment`, {
    method: "DELETE",
    body: JSON.stringify({ payment: { checkout_attempt: checkoutAttempt } }),
  })
}

export function queueSlantaCards(context: ApiContext, orderId: string, retryFailed: boolean) {
  return apiContextFetch<{ order: SlantaOrder }>(context, `slanta/orders/${orderId}/card_drafts`, {
    method: "POST",
    body: JSON.stringify({ card_draft: { retry_failed: retryFailed } }),
  })
}

export function draftSlantaSharePosts(context: ApiContext, orderId: string, regenerate: boolean) {
  return apiContextFetch<{ order: SlantaOrder }>(context, `slanta/orders/${orderId}/share_posts`, {
    method: "POST",
    body: JSON.stringify({ regenerate }),
  })
}
